import { call, delay, fork, put, race, select, take } from 'redux-saga/effects';
import { SagaIterator } from 'redux-saga';

import { OrganizationId } from 'yeself-common/typings/application/Application';

import { respawn } from 'yeself-client-common/utils/SagaUtils';
import log from 'yeself-client-common/utils/LogUtils';

import { fetchReportConfigurationFail, fetchReportConfigurationSuccess } from './AnalyticsActions';
import { selectAnalyticsOrganizationId } from './AnalyticsSelectors';

import { fetchReportConfiguration } from './AnalyticsService';

import {
    FETCH_REPORT_CONFIGURATION_REQUEST,
    FETCH_REPORT_CONFIGURATION_SUCCESS,
    RESET_ANALYTICS_STATE,
    ReportConfiguration,
} from './types';

export function* fetchReportConfigurationTask(organizationId: OrganizationId): SagaIterator<void> {
    try {
        const payload = yield call(fetchReportConfiguration, organizationId);

        yield put(fetchReportConfigurationSuccess(payload?.body));
    } catch (err) {
        log.error(err);

        yield put(fetchReportConfigurationFail());
    }
}

function* fetchReportConfigurationWatcher(): SagaIterator<void> {
    while (true) {
        const { payload } = yield take(FETCH_REPORT_CONFIGURATION_REQUEST);
        yield fork(fetchReportConfigurationTask, payload);
    }
}

function* refreshReportTokenWatcher(): SagaIterator<void> {
    while (true) {
        const { payload }: { payload: ReportConfiguration } = yield take(
            FETCH_REPORT_CONFIGURATION_SUCCESS
        );

        if (!payload.report.expiration) {
            // eslint-disable-next-line no-continue
            continue;
        }

        const tokenExpirationInMs = payload.report.expiration * 1000;

        const { tokenExpired } = yield race({
            tokenExpired: delay(tokenExpirationInMs),
            analyticsReset: take(RESET_ANALYTICS_STATE),
        });

        if (tokenExpired) {
            const organizationId = yield select(selectAnalyticsOrganizationId);

            yield fork(fetchReportConfigurationTask, organizationId);
        }
    }
}

export default function* analyticsSaga() {
    yield respawn(fetchReportConfigurationWatcher);
    yield respawn(refreshReportTokenWatcher);
}
